import { Link } from '@chakra-ui/next-js';
import {
  Box,
  Card,
  CardBody,
  ContainerProps,
  Heading,
  LinkBox,
  LinkOverlay,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { Image } from '@/shared/ui';
import { ProductType } from '../types/ProductType';
import PriceWithDiscount from './PriceWithDiscount';

interface ProductCardProps extends ContainerProps {
  product: ProductType;
}

const ProductCard = ({ product, ...props }: ProductCardProps) => {
  const { t } = useTranslation();
  const image = useMemo(() => {
    const firstImage = product.commonImages.length > 0 ? product.commonImages[0] : null;

    if (firstImage && firstImage.type === 'image') {
      return firstImage;
    }

    const variantImage = product.variants.find((variant) => variant.images.length > 0)?.images[0];

    return variantImage ? variantImage : null;
  }, [product]);

  return (
    <Card
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: 'xl',
      }}
      as={LinkBox}
      bg='white'
      borderRadius='lg'
      boxShadow='md'
      h='full'
      overflow='hidden'
      position='relative'
      transition='all 0.2s'
      {...props}
    >
      <CardBody p='0'>
        <Box
          bg='gray.50'
          h='250px'
          overflow='hidden'
          position='relative'
        >
          {image && (
            <Image
              alt={image.alt}
              h='full'
              src={image.src}
              w='full'
            />
          )}
        </Box>

        <VStack
          align='start'
          p='4'
          spacing='3'
        >
          <LinkOverlay
            as={Link}
            href={`/store/products/${product.slug}`}
          >
            <Heading
              color='gray.800'
              fontSize='lg'
              fontWeight='semibold'
              noOfLines={2}
            >
              {t(product.name)}
            </Heading>
          </LinkOverlay>

          <PriceWithDiscount price={product.price} />
        </VStack>
      </CardBody>
    </Card>
  );
};

export type { ProductCardProps };
export default ProductCard;
